<template>
  <base-modal
    :show="show"
    no-padding
    size="md"
    :custom-class="`${Classes.LOGIN_EMAIL_MODAL} tw-p-8`"
    :cancel-button="false"
    :has-close-btn="hasCloseBtn"
    :auto-focus-modal="false"
    @close:modal="closeLoginModal"
  >
    <div v-if="codeInput" class="tw-space-y-4">
      <div class="tw-relative tw-flex tw-flex-col tw-justify-between">
        <h2 class="tw-mb-4 tw-text-h6 sm:tw-text-h5 tw-text-left">
          {{ $t('signup') }}
        </h2>
        <div class="tw-space-y-4">
          <p class="xs:tw-text-md tw-break-words">
            <i18n-t keypath="otp_email">
              <template #email>
                {{ email }}
              </template>
            </i18n-t>
          </p>
          <p class="tw-text-sm tw-flex">
            {{ t('receive_code') }}
            <base-button
              class="tw-pl-1"
              :status="codeStatus"
              :disabled="codeLoading"
              text-link
              no-padding
              primary
              @click="sendCode(false)"
            >
              {{ t('resend_code') }}
            </base-button>
          </p>
        </div>
      </div>
      <base-divider />
      <div class="tw-space-y-3 tw-flex tw-flex-col tw-justify-center">
        <base-input
          v-if="!codeExpired"
          code
          :model-value="code"
          :errors="validationErrors.code"
          :placeholder="t('code_placeholder')"
          :max="formValidation.code.size"
          focus
          @input="(input) => updateField('code', input)"
        />
        <base-button
          v-if="!codeExpired"
          full-width
          primary
          :status="loginStatus"
          :disabled="!hasCodeInput"
          type="submit"
          @click="login"
        >
          {{ t('login') }}
        </base-button>
        <div
          class="tw-w-full tw-flex tw-flex-col tw-items-center tw-text-center"
        >
          <base-link
            class="tw-text-sm tw-mb-2 tw-font-medium"
            @click="toggleCode()"
          >
            {{ $t('back') }}
          </base-link>
          <panel-error :message="loginErrors" />
        </div>
      </div>
    </div>
    <div v-else>
      <div class="tw-relative tw-w-full">
        <h2 class="tw-mb-4 tw-text-h6 sm:tw-text-h5 tw-text-center">
          {{ $t('signup') }}
        </h2>
        <div class="tw-mb-8">
          <form
            class="tw-pt-2 tw-space-y-2 xs:tw-space-y-4"
            @submit.prevent="login"
          >
            <base-input
              :model-value="email"
              :errors="validationErrors.email"
              :placeholder="t('email_placeholder')"
              class="login-email-input"
              name="email"
              type="email"
              focus
              :max="formValidation.email.max"
              @input="(input) => updateField('email', input)"
            />
            <div class="login-email-button-wrapper tw-space-y-4">
              <base-button
                :status="codeStatus"
                class="send-code-button tw-flex-1 tw-h-11"
                primary
                :disabled="!hasEmailInput || !!validationErrors.email?.length"
                type="submit"
                full-width
                @click="sendCode"
              >
                {{ codeExpired ? t('send_new_code') : t('login_with_email') }}
              </base-button>
              <base-button
                :disabled="!hasEmailInput || !!validationErrors.email?.length"
                text-link-hover
                class="have-code-button tw-flex-1 tw-h-5"
                primary
                full-width
                @click="toggleCode"
              >
                {{ t('have_code') }}
              </base-button>
            </div>

            <panel-error :message="codeErrors" />
          </form>
        </div>
        <div
          class="tw-w-full tw-flex tw-flex-col tw-items-center tw-text-center"
        >
          <base-link
            v-if="showBackButton"
            class="tw-text-sm tw-mb-2 tw-font-medium"
            @click="goBack()"
          >
            {{ $t('back_to_login') }}
          </base-link>

          <term-privacy-modal-link />
        </div>
      </div>
    </div>
  </base-modal>
</template>
<script>
import TermPrivacyModalLink from '@components/Modals/TermPrivacyModalLink.vue'
import { useValidate, useToggle, useStatus, useAsync } from '@composables'
import { loginConfig as formValidation } from '@config/validation.js'
import { requestCodeRequest, loginRequest } from '@services'
import PanelError from '@components/Error/PanelError.vue'
import { LOGIN_METHODS } from '@config/auth.js'
import Classes from '@shared/js/config/css.js'
import { AuthOptions } from '@config/game.js'
import { computed, ref } from 'vue'
import { useI18n } from 'vue-i18n'

export default {
  components: {
    TermPrivacyModalLink,
    PanelError,
  },
  props: {
    show: {
      type: Boolean,
      default: false,
    },
    hasCloseBtn: {
      type: Boolean,
      default: true,
    },
  },
  emits: ['close:loginmodal', 'show:showloginoptions', 'login:success'],
  setup(_, { emit }) {
    const email = ref('')
    const tempEmail = ref('')
    const code = ref('')
    const { t } = useI18n()

    const {
      toggle: toggleCodeInput,
      show: codeInput,
      open: showCodeInput,
      close: hideCodeInput,
    } = useToggle()
    const {
      errors: validationErrors,
      validate,
      updateErrors,
      hasErrors,
    } = useValidate(formValidation.validation)

    const {
      error: codeErrors,
      loading: codeLoading,
      run: requestCodeRun,
      setError: setCodeErrors,
    } = useAsync(() => requestCodeRequest({ email: email.value }))

    const {
      error: loginErrors,
      loading: loginLoading,
      run: loginRun,
      setError: setLoginErrors,
    } = useAsync(() => loginRequest({ code: code.value }))

    const showBackButton = computed(() =>
      LOGIN_METHODS.filter((m) => m.type !== 'email').some(
        (m) => AuthOptions.value[m.config]
      )
    )

    const apiErrors = computed(() => codeErrors.value ?? loginErrors.value)

    const codeExpired = computed(() =>
      apiErrors.value?.includes('code has expired')
    )
    const hasEmailInput = computed(() => email.value !== '')
    const hasCodeInput = computed(() => code.value !== '')

    const {
      updateStatus: updateLoginStatus,
      status: loginStatus,
      statusType,
    } = useStatus({
      loading: loginLoading,
      error: loginErrors,
    })

    const { updateStatus: updateCodeStatus, status: codeStatus } = useStatus({
      loading: codeLoading,
      error: codeErrors,
    })

    async function sendCode(noStatus = true) {
      _clearApiErrors()
      code.value = ''

      if (email.value) {
        validate('email', email.value)

        if (hasErrors('email')) return

        await requestCodeRun()
        if (codeErrors.value) {
          updateErrors(codeErrors.value)
        } else {
          if (!noStatus) updateCodeStatus(statusType.SUCCESS)
          showCodeInput()
          tempEmail.value = email.value
        }
      }
    }

    async function login() {
      if (loginLoading.value) return

      _clearApiErrors()
      validate('code', code.value)
      validate('email', email.value)

      if (hasErrors()) return

      await loginRun()

      if (!loginErrors.value) {
        updateLoginStatus(statusType.SUCCESS)
        closeLoginModal()
        emit('login:success')
      } else {
        tempEmail.value = email.value
      }
    }

    function updateField(field, input) {
      _clearApiErrors()
      if (field === 'email') {
        if (validationErrors.email?.length) {
          validate(field, input)
        }
        email.value = input
        if (codeInput.value) {
          checkTempEmail()
        }
      }

      if (field === 'code') {
        if (
          input.length === 0 ||
          input.length > 5 ||
          validationErrors.code?.length
        ) {
          validate(field, input)
        }
        code.value = input
        if (input.length === 5) {
          setTimeout(login, 500)
        }
      }
    }

    function toggleCode() {
      validate('email', email.value)
      if (hasErrors('email')) return

      toggleCodeInput()
      code.value = ''
    }

    function checkTempEmail() {
      if (tempEmail.value && tempEmail.value !== email.value) {
        hideCodeInput()
        code.value = ''
        tempEmail.value = ''
      }
    }

    function _clearApiErrors() {
      setCodeErrors(null)
      setLoginErrors(null)
    }

    function closeLoginModal() {
      _clearApiErrors()
      email.value = ''
      tempEmail.value = ''
      code.value = ''
      hideCodeInput()
      emit('close:loginmodal')
    }

    function goBack() {
      _clearApiErrors()
      emit('show:showloginoptions')
    }

    return {
      validationErrors,
      closeLoginModal,
      formValidation,
      showBackButton,
      hasEmailInput,
      hasCodeInput,
      codeExpired,
      loginStatus,
      loginErrors,
      updateField,
      codeLoading,
      codeErrors,
      toggleCode,
      codeStatus,
      codeInput,
      sendCode,
      Classes,
      goBack,
      login,
      email,
      code,
      t,
    }
  },
}
</script>
